import { FC, memo, useCallback, useMemo, ReactElement } from "react"
import classNames from "classnames"
import dayjs from "dayjs"
import Changes from "./Changes"
import NumberBadge from "@/components/NumberBadge"
import { Icon, IconColor, IconProps } from "@/components/shared/Icon"
import { TestId } from "@/constants/globals"
import { AlertVariant } from "@/types/alert"
import { AlertSystemConditionType } from "@/types/alertConfigurations"
import { AlarmState, Log, LogSource } from "@/types/logs"
import { localizeDateTime } from "@/utils/index"
import { isEqual } from "@/utils/lodash"
import { getAlarmState } from "@/utils/logs"
import "./index.scss"

export enum LogCardVariant {
   CLEARANCE = "Clearance",
   CHANGE = "Change",
   CREATION = "Creation",
   DELETION = "Deletion",
   TRIGGER = "Trigger",
}

interface LogCardProps extends Log {
   onClick?: () => void
   cardVariant: LogCardVariant
   timezone: string
}

const ALARM_STATE_HEADER: Record<
   AlarmState,
   { title: string; iconName: IconProps["name"] }
> = {
   [AlarmState.ENABLED]: { title: "Alarm enabled", iconName: "alerts" },
   [AlarmState.DISABLED]: { title: "Alarm disabled", iconName: "snoozeAlert" },
}

const LogCard: FC<LogCardProps> = ({
   source,
   alertData,
   alertName,
   configurationPriority,
   cardColor,
   changedBy,
   userName,
   changes,
   event,
   timestamp,
   type,
   cardVariant,
   deviceId,
   hasCustomConfiguration,
   note,
   timezone,
}) => {
   const {
      priority: alertPriority,
      note: alertNote,
      customerEmailNotification: alertEmailNotification,
   } = alertData || {}

   const alarmStateHeader = useMemo(() => {
      if (cardVariant !== LogCardVariant.CHANGE) return null

      const alarmState = getAlarmState(changes)
      return alarmState ? ALARM_STATE_HEADER[alarmState] : null
   }, [cardVariant, changes])

   const noteContent = useMemo(() => {
      const isSystemAlert = alertData?.systemConditionType === null
      const isClearance = cardVariant === LogCardVariant.CLEARANCE
      const isTriggerSystemAlert =
         cardVariant === LogCardVariant.TRIGGER && isSystemAlert

      if (isClearance || isTriggerSystemAlert)
         return alertNote
            ?.replace("heartRate", "BPM")
            .replace("respiratoryRate", "RPM")

      return note
   }, [alertData?.systemConditionType, alertNote, cardVariant, note])

   const footerContent = useMemo(() => {
      const showUsername =
         source === LogSource.CELL ||
         (source === LogSource.INMATE &&
            (cardVariant === LogCardVariant.CHANGE ||
               cardVariant === LogCardVariant.CREATION ||
               cardVariant === LogCardVariant.CLEARANCE))

      return showUsername ? changedBy || userName : null
   }, [cardVariant, changedBy, source, userName])

   const iconName: IconProps["name"] = useMemo(() => {
      switch (cardVariant) {
         case LogCardVariant.CLEARANCE:
            return "checkmark"
         case LogCardVariant.CHANGE:
            return "threshold"
         case LogCardVariant.CREATION:
            return "sensor"
         case LogCardVariant.DELETION:
            return "trashBin"
         case LogCardVariant.TRIGGER:
            return "alerts"
         default:
            return "alert"
      }
   }, [cardVariant])

   const wearableIconName: IconProps["name"] = useMemo(() => {
      switch (cardVariant) {
         case LogCardVariant.CLEARANCE:
            return "checkmark"
         case LogCardVariant.TRIGGER:
            return "alerts"
         case LogCardVariant.CHANGE: {
            const fieldSensorIndex = changes?.findIndex(
               (change) => change.field !== "sensor"
            )
            if (fieldSensorIndex !== -1) return "threshold"

            const fieldDisconnectedIndex = changes?.findIndex(
               (change) => change.oldValue === null && change.newValue !== null
            )
            if (fieldDisconnectedIndex !== -1) {
               return "connected"
            }
            return "unlinked"
         }
         default:
            return "threshold"
      }
   }, [cardVariant, changes])

   const hideChanges = useMemo(() => {
      if (source === LogSource.CELL) return false

      const fieldDisconnectedIndex = changes?.findIndex(
         (change) => change.field === "sensor"
      )
      return (
         fieldDisconnectedIndex !== -1 && cardVariant === LogCardVariant.CHANGE
      )
   }, [cardVariant, changes, source])

   const getAlertVariantClassName = useCallback(
      (alertVariant: keyof typeof AlertVariant) => {
         if (!alertVariant) {
            return ""
         }
         return `--${AlertVariant[alertVariant]}`
      },
      []
   )

   const colorVariant = useMemo(() => {
      const takesAlarmColor =
         cardVariant === LogCardVariant.CLEARANCE ||
         cardVariant === LogCardVariant.TRIGGER ||
         alarmStateHeader !== null

      if (!takesAlarmColor) return ""

      return getAlertVariantClassName(cardColor as keyof typeof AlertVariant)
   }, [getAlertVariantClassName, cardColor, cardVariant, alarmStateHeader])

   const title = useMemo(() => {
      if (alarmStateHeader) return alarmStateHeader.title

      return event.charAt(0).toUpperCase() + event.slice(1).toLowerCase()
   }, [alarmStateHeader, event])

   const cardIconName = useMemo((): IconProps["name"] => {
      if (alarmStateHeader) return alarmStateHeader.iconName

      return source === LogSource.CELL ? iconName : wearableIconName
   }, [alarmStateHeader, iconName, source, wearableIconName])

   const systemAlertHeaderContent = useMemo((): {
      iconName: IconProps["name"]
      title: string
   } | null => {
      if (
         cardVariant === LogCardVariant.TRIGGER &&
         alertData?.systemConditionType
      ) {
         switch (alertData.systemConditionType) {
            case AlertSystemConditionType.NO_VITALS:
               if (source === LogSource.CELL) {
                  return {
                     iconName: "noVitalSign",
                     title: "No Vitals / Empty Cell",
                  }
               }
               return { iconName: "noVitalSign", title: "No Vitals" }
            case AlertSystemConditionType.BROKEN_WEARABLE:
               return { iconName: "brokenWatch", title: "Tamper Alarm" }
            default:
               return null
         }
      }
      return null
   }, [alertData?.systemConditionType, cardVariant, source])

   const alertWearableContent = useMemo((): ReactElement | null => {
      if (
         cardVariant !== LogCardVariant.TRIGGER ||
         source !== LogSource.INMATE
      ) {
         return null
      }

      if (deviceId == null && alertData?.levelName == null) return null

      return (
         <>
            {deviceId != null && (
               <div>
                  <div className="log-card__title">ReassureBand</div>
                  <div>{deviceId}</div>
               </div>
            )}
            {alertData?.levelName != null && (
               <div>
                  <div className="log-card__title">Location</div>
                  <div>{alertData.levelName}</div>
               </div>
            )}
         </>
      )
   }, [alertData?.levelName, cardVariant, deviceId, source])

   const alarmNameContent = useMemo((): ReactElement | null => {
      if (!alarmStateHeader) return null

      const alarmName = source === LogSource.INMATE ? event : alertName
      if (!alarmName) return null

      return (
         <div>
            <div className="log-card__title">Alarm name</div>
            <div>{alarmName}</div>
         </div>
      )
   }, [alarmStateHeader, alertName, event, source])

   const alarmTraceIdContent = useMemo((): ReactElement | null => {
      const isTriggerOrClearance =
         cardVariant === LogCardVariant.TRIGGER ||
         cardVariant === LogCardVariant.CLEARANCE

      if (!isTriggerOrClearance || alertData?.traceId == null) return null

      return (
         <div>
            <div className="log-card__title">Alarm trace id</div>
            <div className="log-card__trace-id">{alertData.traceId}</div>
         </div>
      )
   }, [alertData?.traceId, cardVariant])

   return (
      <div
         className={classNames("log-card", {
            [`log-card__border-color${colorVariant}`]: colorVariant,
         })}
      >
         <span
            className={classNames("log-card__icon", {
               [`log-card__icon-background${colorVariant}`]: colorVariant,
            })}
         >
            <Icon
               name={cardIconName}
               color={IconColor.WHITE}
               size="16"
               data-testid={TestId.LOG_CARD_ICON}
            />
         </span>
         <span className="log-card__content">
            <span className="log-card__header">
               {cardVariant !== LogCardVariant.CREATION &&
                  (alertPriority ?? configurationPriority) !== undefined && (
                     <NumberBadge
                        number={alertPriority ?? configurationPriority ?? 0}
                        alertVariant={cardColor as keyof typeof AlertVariant}
                     />
                  )}
               {hasCustomConfiguration && (
                  <Icon
                     name="cellCustom"
                     color={IconColor.COOL_GRAY}
                     size="16"
                  />
               )}
               <span>{title}</span>
               {alertEmailNotification && (
                  <Icon
                     name="envelope"
                     color={IconColor.HONOLULU_BLUE}
                     size="12"
                     className="log-card__email-icon"
                  />
               )}
            </span>
            {systemAlertHeaderContent && (
               <span className="log-card__system-alert-header">
                  <Icon
                     name={systemAlertHeaderContent.iconName}
                     color={IconColor.HONOLULU_BLUE_DARK}
                     size="18"
                  />
                  <span>{systemAlertHeaderContent.title}</span>
               </span>
            )}
            {noteContent && <div className="log-card__note">{noteContent}</div>}
            {alertWearableContent}
            {alarmNameContent}
            {alarmTraceIdContent}
            {!hideChanges && changes && (
               <Changes
                  changes={changes}
                  type={type}
                  getAlertVariantClassName={getAlertVariantClassName}
               />
            )}
            <span className="log-card__footer">
               <span>{localizeDateTime(dayjs(timestamp), timezone)}</span>
               {footerContent && <span>{footerContent}</span>}
            </span>
         </span>
      </div>
   )
}

export default memo(LogCard, (prevProps, nextProps) => {
   const keys: (keyof LogCardProps)[] = [
      "alertData",
      "alertName",
      "configurationPriority",
      "cardColor",
      "changedBy",
      "userName",
      "changes",
      "event",
      "timestamp",
      "type",
      "cardVariant",
      "deviceId",
      "hasCustomConfiguration",
      "timezone",
   ]
   return keys.every((key) => isEqual(prevProps[key], nextProps[key]))
})
